<template lang='pug'>
  .site-search(ref="el" tabindex="-1" @focusout="hide($event)")
    // We use the a11yLinks component for accessible site search - so these are all tabindex -1
    form(role="search" :aria-label="__('Site search')" @submit.prevent.stop="" autocomplete="off" tabindex="-1" data-cnstrc-search-form)
      label.rogue-hidden-semantic-label(for="search-bar-input") {{ __('Search Bar') }}
      input.search-input(
        :class="keyphrase && 'is-keyphrase'"
        tabindex="-1"
        id="search-bar-input"
        @focusin="show()"
        @keyup.enter="goToResults()"
        ref="searchbar"
        :placeholder="__('Search')"
        autocomplete="off"
        :value="keyphrase || query"
        @input="keyphrase = null; query = $event.target.value"
        data-cnstrc-search-input
      )
    button.a11y.search-icon(
      @click="goToResults()"
      tabindex="-1"
      aria-label="Search"
      role="button"
      data-cnstrc-search-submit-btn
    )
      Icon(name="search")
    button.a11y.close-icon(@click="hide()" aria-label="Hide")
      Icon.close-icon(name="x")
    .search-results(data-track="Site Search")
      .results
        SearchResults(
          :active="true"
          :query="keyphrase || query"
          @update:phrase="keyphrase = $event"
          @update:redirect="redirect = $event"
        )
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, useTemplateRef } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { allowScroll, preventScroll } from 'sisu/js/body-scroll'
import { useMQ } from 'sisu/js/mq'
import { useRogue } from '@/js/services/Rogue'
import SearchResults from '@/js/components/header/search/search-results.vue'

const props = withDefaults(defineProps<{
  autoOpen?: boolean
}>(), {
  autoOpen: false,
})

const emit = defineEmits<{
  close: []
}>()

const router = useRouter()
const route = useRoute()
const rogue = useRogue()
const mq = useMQ()

const query = ref<string>('')
const showing = ref<boolean>(false)
const keyphrase = ref<string | null>(null)
const redirect = ref<string | null>(null)

const el = useTemplateRef<HTMLElement>('el')
const searchbar = useTemplateRef<HTMLInputElement>('searchbar')

const viewAll = computed(() => `/search?q=${encodeURIComponent(query.value)}`)

watch(mq, (newVal, oldVal) => {
  if (showing.value && newVal !== 'mobile' && oldVal === 'mobile') {
    hide()
  }
})

watch(route, () => {
  keyphrase.value = null
  query.value = ''
  if (showing.value) {
    hide()
  }
})

watch(() => route.query, () => {
  if (showing.value) {
    hide()
  }
})

// Only the Competition.vue search is destroyed... normally, the header search lives forever
onMounted(() => {
  if (props.autoOpen) {
    searchbar.value?.focus()
  }
})

onBeforeUnmount(() => {
  allowScroll()
})

function show() {
  if (!showing.value) {
    showing.value = true
    if (mq.value === 'mobile') {
      el.value?.scrollIntoView({ block: 'start' })
      preventScroll()
    }
  }
}

function hide(evt?: FocusEvent) {
  // currentTarget is the parent element, relatedTarget is the clicked element
  if (!evt || !(evt.currentTarget as Element).contains(evt.relatedTarget as Node)) {
    keyphrase.value = null
    showing.value = false
    if (mq.value === 'mobile') {
      allowScroll()
      el.value?.closest('.main-header')?.scrollIntoView({ block: 'start' })
    }
    ;(document.activeElement as HTMLElement)?.blur()
  }
  setTimeout(() => emit('close'), 300)
}

function goToResults() {
  if (redirect.value) {
    rogue.track.track({
      type     : 'previewSearchSelection' as any,
      redirect : redirect.value,
      query    : query.value,
    })
    router.push(redirect.value)
  } else if (query.value.length) {
    router.push(viewAll.value)
  }
  query.value = ''
  hide()
}
</script>

<style lang='scss' scoped>
.site-search {
  position: relative;
  flex-grow: 1;
  z-index: 1;

  @include tablet() {
    margin: 0 20px;
  }
  @include all-desktop() {
    margin: 0 40px;
  }
}

@include mobile() {
  .site-search {
    padding: $space-xxs;
    margin-top: -2px; //@todo why is this necessary? Something was adding 2px...
  }
}

button.search-icon {
  position: absolute;
  top: $space-xxs;
  left: $space-xxs;
  height: 38px;
  width: 38px;
  cursor:pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-black;

  @include not-mobile() {
    top: 0;
    left: auto;
    right: 0;
  }
}

.close-icon {
  display: none;
  position: absolute;
  top: $space-xxs;
  right: $space-m;
  height: 38px;
}

.search-input {
  width: 100%;
  padding: $space-xxs;
  padding-right: $space-xxl;
  font-size: $size-s;

  &.is-keyphrase {
    color: $color-gray-dark;
  }

  @include mobile() {
    padding-left: 44px;
  }
}

.results {
  flex-grow: 1;
  position: relative;
  @include mobile(){
    position: fixed;
    padding: 0 $space-xs;
    top: 50px;
    left: 0;
    width: 100%;
    bottom: $space-xs;
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.search-results {
  display: none;
  top: 100%;
  width: 100vw;
  left: 0;
  z-index: 2;
  color: black;
  background-color: white;

  @include not-mobile() {
    max-width: 700px;
    box-shadow: 0 1px 2px $color-gray;
    position: absolute;
    left: calc(50% - 24px);
    transform: translateX(-50vw);
  }

  @include mobile() {
    padding: 46px $space-xs $space-xs $space-xs;
    position: fixed;
    height: 100%;
    width: 100%;
    overflow-scrolling: touch;
    top: 0;
    left: 0;
    z-index: -1;
  }

  @media (min-width: $desktop) {
    left: 0;
    transform: none;
  }

}

.site-search:focus-within {
  z-index: 101;

  .search-results {
    display: block;
  }

  @include mobile() {
    .close-icon {
      right: 10px;
      height: 25px;
      cursor: pointer;
      display: block;
    }
  }
}

</style>
